import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// internal rebet imports
import "./stylesPoker.css";
import createPrivateAdminPanelInstance from "../../../../api/PrivateAdminPanelInstance";
import { API_ROUTES } from "../../../../api/ApiRoutes";

// component imports
import RebetLoader from "../../../CommonComponents/RebetLoader/RebetLoader";

// asset imports
import { KPI_CARD_BACKGROUND } from "../../../../utils/Constants";

import cashIcon from "../../../../assets/images/cash.png";
import coinIcon from "../../../../assets/images/coins.png";

import SaveButton from "../../../CommonComponents/SaveButton/SaveButton";
import KpiCard from "../../../CommonComponents/Dashboard/KpiCard/KpiCard";
import CustomDatePicker from "../../../CommonComponents/CustomDatePicker/CustomDatePicker";

import { Input, Table, Pagination, Select, Button, message } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";

import { EyeOutlined } from "@ant-design/icons";

function PokerTable({ userData, setActiveSection, setHandData }) {
  const token = JSON.parse(localStorage.getItem("token"));
  const PrivateAdminPanelInstance = createPrivateAdminPanelInstance(
    token?.IdToken
  );
  const navigate = useNavigate();

  // state variables
  const [searchValue, setSearchValue] = useState("");
  const [isApiRunning, setIsApiRunning] = useState(false);
  const [resetTable, setResetTable] = useState(false);
  const todayRange = [dayjs().startOf("day"), dayjs().endOf("day")];
  const [casinoBetsDates, setCasinoBetsDates] = useState(null);
  const [summary, setSummary] = useState({
    total_buy_in: 0,
    total_cash_out: 0,
    total_net_revenue: 0,
  });
  const [betsCache, setBetsCache] = useState({});
  const [moneyType, setMoneyType] = useState("rebet_cash");
  const [moneyTypeOptions, setMoneyTypeOptions] = useState([
    { label: "Cash", value: "rebet_cash" },
    { label: "Coin", value: "rebet_coin" },
  ]);
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 20,
    total_results: 0,
    total_pages: 0,
    has_next: false,
    has_prev: false,
  });
  const [filterType, setFilterType] = useState("rebet_cash");

  const handleFilterTypeChange = (value) => {
    setFilterType(value);
  };

  // use effects
  useEffect(() => {
    getUserPokerGames(1, true, todayRange, false, true);
  }, [userData]);

  // api functions
  const getUserPokerGames = async (
    page = 1,
    refresh = false,
    queryString = todayRange,
    isQueryString = true,
    allTime = false
  ) => {
    setIsApiRunning(true);

    let startDate = "";
    let endDate = "";

    if (isQueryString) {
      let formattedQueryString = queryString.split("&");
      startDate = dayjs(formattedQueryString[0]).format("YYYY-MM-DD");
      endDate = dayjs(formattedQueryString[1]).format("YYYY-MM-DD");
    } else if (allTime) {
      startDate = null;
      endDate = null;
    } else {
      startDate = queryString[0]?.format("YYYY-MM-DD");
      endDate = queryString[1]?.format("YYYY-MM-DD");
    }

    // Params for games list
    const gamesParams = {
      user_id: userData?.user_id,
      start_date: startDate,
      end_date: endDate,
      page: page,
      money_type: moneyType,
    };
    // Params for stats summary
    const statsParams = {
      user_id: userData?.user_id,
      money_type: moneyType,
    };

    try {
      const requests = [
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_USER_POKER_STATS,
          statsParams
        ),
        PrivateAdminPanelInstance?.post(
          API_ROUTES?.GET_USER_POKER_GAMES,
          gamesParams
        ),
      ];

      const responses = await Promise.allSettled(requests);

      const [pokerStatsResponse, pokerGamesResponse] = responses;

      if (pokerStatsResponse.status === "fulfilled") {
        const data = pokerStatsResponse?.value?.data?.data;
        setSummary({
          total_buy_in: data?.total_buy_in || 0,
          total_cash_out: data?.total_cash_out || 0,
          total_net_revenue: data?.net_revenue || 0,
        });
      } else {
        setSummary({ total_buy_in: 0, total_cash_out: 0, total_net_revenue: 0 });
        message.info("Poker stats unavailable for selected money type.");
      }
      if (pokerGamesResponse.status === "fulfilled") {
        const data = pokerGamesResponse?.value?.data?.data?.data;
        const pagination = pokerGamesResponse?.value?.data?.data?.pagination;

        const formattedData = (Array.isArray(data) ? data : []).map((game) => {
          return {
            ...game,
            game_start_time: dayjs(game.game_start_time).format(
              "MM/DD/YYYY - HH:mm"
            ),
            player_join_time: dayjs(game.player_join_time).format("HH:mm"),
            player_leave_time: dayjs(game.player_leave_time).format("HH:mm"),
          };
        });
        setPagination(pagination);
        setPokerGames(formattedData);
        if (!Array.isArray(data) || data.length === 0) {
          message.open({
            type: "info",
            content: "No poker games found for the selected period.",
            key: "poker-empty",
          });
        }
      } else {
        message.error("Failed to fetch poker games.");
        setPokerGames([]);
      }
    } catch (error) {
      console.log("error fetching users poker games", error);
      message.error("Something went wrong fetching poker games.");
      setIsApiRunning(false);
    } finally {
      setIsApiRunning(false);
    }
  };

  const searchBet = async () => {
    setIsApiRunning(true);

    if (!searchValue) {
      message.warning("Please enter a Game ID to search.");
      setIsApiRunning(false);
      return;
    }

    const params = {
      user_id: userData?.user_id,
      game_id: searchValue,
      money_type: moneyType,
    };
    try {
      const response = await PrivateAdminPanelInstance?.post(
        API_ROUTES?.GET_USER_POKER_GAMES,
        params
      );

      const bet = response?.data?.data?.data;
      const formattedData = (Array.isArray(bet) ? bet : []).map((game) => {
        return {
          ...game,
          game_start_time: dayjs(game.game_start_time).format(
            "MM/DD/YYYY - HH:mm"
          ),
          player_join_time: dayjs(game.player_join_time).format("HH:mm"),
          player_leave_time: dayjs(game.player_leave_time).format("HH:mm"),
        };
      });
      setPokerGames(formattedData);
      if (!Array.isArray(bet) || bet.length === 0) {
        message.open({
          type: "info",
          content: "No poker games match that Game ID.",
          key: "poker-search-empty",
        });
      }
      setPagination({
        current_page: 1,
        page_size: 20,
        total_results: 0,
        total_pages: 0,
        has_next: false,
        has_prev: false,
      });
    } catch (error) {
      console.log("error searching for poker game", error);
      message.error("Failed to search poker games.");
    } finally {
      setIsApiRunning(false);
    }
  };

  // helper functions
  const handleSearchValueChange = (event) => {
    setSearchValue(event?.target?.value);
  };

  const handleSearchPressed = () => {
    searchBet();
  };

  const [pokerGames, setPokerGames] = useState([]);

  const columns = [
    {
      title: "",
      dataIndex: "game_id",
      key: "game_id",
      width: 50,
      render: (text, record) => {
        return (
          <div className="poker-game-go-to-hands-table-container">
            <EyeOutlined
              style={{ cursor: "pointer", fontSize: "14px" }}
              onClick={() => {
                setActiveSection("Hand Table");
                setHandData(record);
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Game ID",
      dataIndex: "game_id",
      key: "game_id",
      width: 300,
      render: (text) => {
        return <p className="game-id-text">{text}</p>;
      },
    },
    {
      title: "Start Time",
      dataIndex: "game_start_time",
      key: "game_start_time",
      width: 150,
    },
    {
      title: "Player join time",
      dataIndex: "player_join_time",
      key: "player_join_time",
      width: 150,
    },
    {
      title: "Leave time",
      dataIndex: "player_leave_time",
      key: "player_leave_time",
      width: 150,
    },
    {
      title: "Buy in amount",
      dataIndex: "total_buy_in",
      key: "total_buy_in",
      width: 150,
      render: (text) => {
        return (
          <p style={{ margin: 0 }}>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(text)}
          </p>
        );
      },
    },
    {
      title: "Cashout amount",
      dataIndex: "total_cash_out",
      key: "total_cash_out",
      width: 200,
      render: (text) => {
        return (
          <p style={{ margin: 0 }}>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(text)}
          </p>
        );
      },
    },

    {
      title: "Nickname",
      dataIndex: "nickname",
      key: "nickname",
      width: 150,
    },
    {
      title: "Seat Number",
      dataIndex: "seat_number",
      key: "seat_number",
      width: 150,
    },
    {
      title: "Number of Hands played",
      dataIndex: "hands_played",
      key: "hands_played",
      width: 250,
    },
    {
      title: "Game Turnover",
      dataIndex: "total_game_turnover",
      key: "total_game_turnover",
      width: 150,
      render: (text) => {
        return (
          <p style={{ margin: 0 }}>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(text)}
          </p>
        );
      },
    },
    {
      title: "Game P&L",
      dataIndex: "profit",
      key: "profit",
      width: 150,
      render: (text) => {
        return (
          <p style={{ margin: 0 }}>
            {Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(text)}
          </p>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "game_status",
      key: "game_status",
      width: 150,
    },
    {
      // title: () => (
      //   <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
      //     <span>Bet Amount</span>
      //     <div className="bet-amount-buttons">
      //       <Button
      //         className={filterType === "rebet_cash" ? "active" : ""}
      //         onClick={() => setFilterType("rebet_cash")}
      //       >
      //         <img src={cashIcon} alt="Cash" />
      //       </Button>
      //       <Button
      //         className={filterType === "rebet_coin" ? "active" : ""}
      //         onClick={() => setFilterType("rebet_coin")}
      //       >
      //         <img src={coinIcon} alt="Coin" />
      //       </Button>
      //     </div>
      //   </div>
      // ),
      title: "Bet Amount",
      dataIndex: "bet_amount",
      key: "bet_amount",
      width: 250,
      render: (text, record) => (
        <div className="bets-report-bet-amount-container">
          {record.currency_type === "rebet_cash" ? (
            <img src={cashIcon} alt="Cash" />
          ) : record.currency_type === "rebet_coin" ? (
            <img src={coinIcon} alt="Coin" />
          ) : (
            ""
          )}
          <span className="bets-report-bet-amount-text">
            {record.bet_amount === "-" || !record.bet_amount
              ? "-"
              : Intl.NumberFormat("en-US", {
                  style: "decimal",
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(record.bet_amount)}
          </span>
        </div>
      ),
      onCell: () => ({
        style: {
          verticalAlign: "top",
        },
      }),
    },
  ];

  const handleDateChange = (queryString) => {
    getUserPokerGames(1, true, queryString, true, false);
  };

  const handleResetTable = () => {
    setCasinoBetsDates(null);
    getUserPokerGames(1, true, todayRange, false, true);
  };

  const handlePaginationChange = (page) => {
    let allTimeParams = casinoBetsDates ? false : true;
    let queryStringParams = casinoBetsDates ? casinoBetsDates : todayRange;

    getUserPokerGames(page, false, queryStringParams, false, allTimeParams);
  };

  const handleChangeMoneyType = (value) => {
    setMoneyType(value);
    // Refetch with the current date filters using the new money type
    const allTimeParams = casinoBetsDates ? false : true;
    const queryStringParams = casinoBetsDates ? casinoBetsDates : todayRange;
    getUserPokerGames(1, true, queryStringParams, false, allTimeParams);
  };

  return (
    <div className="poker-bets-container">
      <div className="poker-bets-header">
        <div className="kpi-cards-container">
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Buy in"
            value={summary?.total_buy_in}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Total Cash Out"
            value={summary?.total_cash_out}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
          <KpiCard
            isApiRunning={isApiRunning}
            title="Player Net Revenue"
            value={summary?.total_net_revenue}
            hugeValue={false}
            percentage={false}
            backgroundColor={KPI_CARD_BACKGROUND.DARK}
          />
        </div>
        <CustomDatePicker
          getData={handleDateChange}
          dates={casinoBetsDates}
          setDates={setCasinoBetsDates}
          showPresets={true}
          startRange="Today"
        />
      </div>
      <div className="poker-bets-table-container">
        <div className="head-controls poker-bets-table-header-container">
          <div className="header-text-container">
            <p className="header-text">{userData?.username}'s Poker Games</p>
          </div>

          <Input
            type="text"
            placeholder="Search by Game ID"
            className="bet-searchInput"
            value={searchValue}
            onChange={handleSearchValueChange}
          />
          <div className="drop-down-container">
            <div className="casino-selector">
              <Select
                placeholder="Money Type"
                options={moneyTypeOptions}
                value={moneyType}
                onChange={handleChangeMoneyType}
              />
            </div>
          </div>
          <button className="search-button" onClick={handleSearchPressed}>
            <SearchOutlined style={{ fontSize: "20px", color: "white" }} />
          </button>
        </div>

        <div className="">
          {isApiRunning ? (
            <RebetLoader height={"100px"} width={"100px"} />
          ) : (
            <div className="poker-bets-table">
              <Table
                dataSource={pokerGames}
                columns={columns}
                rowKey={(record) => record.game_id}
                scroll={{ x: 2000, y: 500 }}
                pagination={false}
                locale={{ emptyText: "No poker games found." }}
                onRow={(record) => ({
                  className: "poker-bets-table-row",
                })}
              />
              {resetTable ? (
                <div className="reset-table-button-container">
                  <SaveButton
                    buttonText="Reset Table"
                    className="reset-table-button"
                    callbackFunction={handleResetTable}
                    isLoading={isApiRunning}
                  />
                </div>
              ) : (
                !isApiRunning && pokerGames?.length > 0 && (
                  // (pagination?.has_next_page ||(
                  <div className="sports-bets-pagination-container">
                    <Pagination
                      current={pagination.current_page}
                      total={pagination.total_games}
                      pageSize={pagination.per_page}
                      showSizeChanger={false}
                      onChange={(page) => {
                        if (
                          page === pagination.current_page - 1 ||
                          page === pagination.current_page + 1
                        ) {
                          handlePaginationChange(page);
                        }
                      }}
                      itemRender={(page, type, originalElement) => {
                        if (type === "page") {
                          return (
                            <a
                              rel="nofollow"
                              disabled={page > pagination.current_page + 1}
                              className={`${
                                page > pagination.current_page + 1
                                  ? "disabled"
                                  : ""
                              }`}
                            >
                              {page}
                            </a>
                          );
                        }
                        return originalElement;
                      }}
                    />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default PokerTable;
